import { motion } from "framer-motion";
import { ReactElement } from "react";

import { EntityUpsert } from "../api";
import IconCodeDots from "../assets/icons/code-dots.svg";
import { colorSchemas } from "../config/entities.ts";
import { MAX_NODE_ENTITY_TYPE_TITLE_LENGTH } from "../constants.ts";
import { truncate } from "../utils/truncate-string.ts";
import { IconRenderer } from "./icons/IconRenderer.tsx";

interface NodePreviewProps {
  animated?: boolean;
  entity: "skeleton" | EntityUpsert;
}

export const NodePreview = ({
  animated = true,
  entity,
}: NodePreviewProps): ReactElement => {
  const isSkeleton = entity === "skeleton";
  const isStickyNote =
    !isSkeleton && entity.qualifiedName === "native.sticky-note";

  const colors = isSkeleton
    ? colorSchemas["neutral"]
    : colorSchemas[entity.colorSchema];

  const animationProps = animated
    ? {
        animate: {
          opacity: 1,
          rotate: [-2, 2, -2],
          scale: 1,
        },
        initial: { opacity: 0, scale: 0.98 },
        transition: {
          duration: 0.2,
          rotate: {
            duration: 2,
            ease: "easeInOut",
            repeat: Infinity,
            times: [0, 0.5, 1],
          },
        },
      }
    : {
        animate: { opacity: 1 },
        initial: { opacity: 1 },
      };

  const innerAnimationProps = animated
    ? {
        animate: {
          boxShadow: [
            "0 6px 12px rgba(0,0,0,0.15)",
            "0 4px 8px rgba(0,0,0,0.1)",
            "0 6px 12px rgba(0,0,0,0.15)",
          ],
          scale: [1.01, 1, 1.01],
          y: [-2, 0, -2],
        },
        transition: {
          boxShadow: {
            duration: 2,
            ease: "easeInOut",
            repeat: Infinity,
            times: [0, 0.5, 1],
          },
          scale: {
            duration: 2,
            ease: "easeInOut",
            repeat: Infinity,
            times: [0, 0.5, 1],
          },
          y: {
            duration: 2,
            ease: "easeInOut",
            repeat: Infinity,
            times: [0, 0.5, 1],
          },
        },
      }
    : {};

  // For sticky notes, show just a simple icon
  if (isStickyNote) {
    return (
      <motion.div
        {...animationProps}
        className="flex origin-top select-none items-center justify-center"
      >
        <motion.div
          {...innerAnimationProps}
          className="flex h-12 w-12 items-center justify-center rounded-lg border-2"
          style={{
            backgroundColor: "#374151", // Dark grey background to match graph
            borderColor: "#6B7280", // Grey border
          }}
        >
          <IconRenderer
            icon={entity.icon}
            style={{
              color: "#FFFFFF", // White text to match graph
              height: "20px",
              width: "20px",
            }}
          />
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      {...animationProps}
      className="flex origin-top select-none flex-col items-center"
    >
      <motion.div
        {...innerAnimationProps}
        className="relative mb-2 flex h-14 w-16 flex-col items-center justify-center rounded-xl border-2 align-middle"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border,
        }}
      >
        {isSkeleton ? (
          <IconCodeDots
            style={{
              color: "#fff",
              height: "24px",
              width: "24px",
            }}
          />
        ) : (
          <IconRenderer
            icon={entity.icon}
            style={{
              color: "#fff",
              height: "24px",
              width: "24px",
            }}
          />
        )}

        <motion.span className="whitespace-nowrap text-xs text-white opacity-75">
          {isSkeleton
            ? "Loading"
            : truncate(entity.name, MAX_NODE_ENTITY_TYPE_TITLE_LENGTH)}
        </motion.span>
      </motion.div>

      <motion.span className="text-xs text-white opacity-75">
        {isSkeleton
          ? "Loading"
          : entity.defaultValue
            ? entity.defaultValue
            : "<value>"}
      </motion.span>
    </motion.div>
  );
};
